<template>
    <div class="invoice-doc">
  
      <!-- Loading -->
      <div v-if="loading" class="invoice-loading">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3">Loading invoice...</p>
      </div>
  
      <template v-else>
  
        <!-- ── Header ── -->
        <div class="invoice-header">
          <div class="invoice-header-left">
            <p class="invoice-label">INVOICE</p>
            <p class="invoice-number">#{{ invoice?.invoice_no }}</p>
            <span class="invoice-status-chip" :class="statusClass(invoice?.status)">
              {{ getStatus(invoice?.status) }}
            </span>
          </div>
          <div class="invoice-header-right">
            <img v-if="company?.logo" :src="host + company?.logo" alt="Company Logo" class="invoice-logo" />
            <p class="company-name">{{ company?.name }}</p>
          </div>
        </div>
  
        <!-- ── Parties ── -->
        <div class="invoice-parties">
  
          <div class="party-card">
            <p class="party-label">Bill To</p>
            <p class="party-name">{{ invoice?.customer?.customer_name }}</p>
            <p class="party-detail" v-if="invoice?.customer?.address">{{ invoice.customer.address }}</p>
            <p class="party-detail" v-if="invoice?.customer?.email">{{ invoice.customer.email }}</p>
          </div>
  
          <div class="party-card">
            <p class="party-label">From</p>
            <p class="party-name">{{ company?.name }}</p>
            <p class="party-detail" v-if="company?.address">{{ company.address }}</p>
            <p class="party-detail" v-if="company?.email">{{ company.email }}</p>
            <p class="party-detail" v-if="company?.phone_number">{{ company.phone_number }}</p>
          </div>
  
          <div class="party-card">
            <p class="party-label">Invoice Details</p>
            <div class="meta-row"><span class="meta-key">Invoice Date</span><span class="meta-val">{{ dateTime(invoice?.invoice_date) }}</span></div>
            <div class="meta-row"><span class="meta-key">Due Date</span><span class="meta-val">{{ dateTime(invoice?.due_date) }}</span></div>
            <div class="meta-row"><span class="meta-key">Currency</span><span class="meta-val">{{ invoice?.currency?.name }} ({{ invoice?.currency?.symbol }})</span></div>
          </div>
  
        </div>
  
        <!-- ── Line Items ── -->
        <div class="invoice-section">
          <table class="inv-table">
            <thead>
              <tr>
                <th style="width:40%">Description</th>
                <th class="tc">Qty</th>
                <th class="tr">Price ({{ invoice?.currency?.symbol }})</th>
                <th class="tr">Tax ({{ invoice?.currency?.symbol }})</th>
                <th class="tr">Amount ({{ invoice?.currency?.symbol }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in invoice_array" :key="i">
                <td class="item-desc">{{ item?.description }}</td>
                <td class="tc text-muted-cell">{{ item?.quantity }}</td>
                <td class="tr">{{ $filters.formatMoney(item?.price) }}</td>
                <td class="tr text-muted-cell">
                  <span v-if="item.apply_tax">{{ $filters.formatMoney(item?.tax_amount) }}</span>
                  <span v-else class="na-badge">N/A</span>
                </td>
                <td class="tr fw-bold">{{ $filters.formatMoney(item?.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- ── Totals ── -->
        <div class="invoice-totals-wrap">
          <div class="invoice-totals">
            <div class="totals-row">
              <span class="totals-label">Subtotal</span>
              <span class="totals-value">{{ $filters.formatMoney(subTotal, invoice?.currency?.symbol) }}</span>
            </div>
            <div class="totals-row" v-if="discountTotal > 0">
              <span class="totals-label">Discount</span>
              <span class="totals-value deduction-value">− {{ $filters.formatMoney(discountTotal, invoice?.currency?.symbol) }}</span>
            </div>
            <div class="totals-row" v-if="taxTotal > 0">
              <span class="totals-label">Tax<template v-if="invoice?.tax?.tax_name"> ({{ invoice.tax.tax_name }})</template></span>
              <span class="totals-value">{{ $filters.formatMoney(taxTotal, invoice?.currency?.symbol) }}</span>
            </div>
            <div class="totals-divider"></div>
            <div class="totals-row totals-row--grand">
              <span class="totals-label-grand">Total</span>
              <span class="totals-value-grand">{{ $filters.formatMoney(grandTotal, invoice?.currency?.symbol) }}</span>
            </div>
          </div>
        </div>
  
        <!-- ── Payment Info ── -->
        <div class="invoice-footer-sections">
  
          <div class="footer-section">
            <p class="footer-section-title"><font-awesome-icon icon="money-bill-wave" class="me-2" />Payment Summary</p>
            <div class="footer-row">
              <span>Total Amount</span>
              <span class="fw-bold">{{ $filters.formatMoney(invoice?.total_amount, invoice?.currency?.symbol) }}</span>
            </div>
            <div class="footer-row">
              <span>Amount Paid</span>
              <span class="fw-bold earning-value">{{ $filters.formatMoney(invoice?.paid_amount, invoice?.currency?.symbol) }}</span>
            </div>
            <div class="footer-row" :class="invoice?.balance_amount > 0 ? 'footer-row--outstanding' : ''">
              <span>Balance Due</span>
              <span class="fw-bold" :class="invoice?.balance_amount > 0 ? 'deduction-value' : 'earning-value'">
                {{ $filters.formatMoney(invoice?.balance_amount ?? 0, invoice?.currency?.symbol) }}
              </span>
            </div>
          </div>
  
          <!-- Bank Details -->
          <div class="footer-section" v-if="invoice?.bank && invoice?.balance_amount > 0">
            <p class="footer-section-title"><font-awesome-icon icon="university" class="me-2" />Bank Details</p>
            <div class="footer-row">
              <span>Bank Name</span>
              <span class="fw-bold">{{ invoice?.bank?.bank_name }}</span>
            </div>
            <div class="footer-row">
              <span>Account Name</span>
              <span class="fw-bold">{{ invoice?.bank?.account_name }}</span>
            </div>
            <div class="footer-row">
              <span>Account Number</span>
              <span class="fw-bold">{{ invoice?.bank?.account_no }}</span>
            </div>
          </div>
  
          <!-- Online Payment -->
          <div class="footer-section" v-if="invoice?.balance_amount > 0 && invoice?.payment_link">
            <p class="footer-section-title"><font-awesome-icon icon="link" class="me-2" />Pay Online</p>
            <a :href="invoice?.payment_link" target="_blank" class="payment-link">
              <font-awesome-icon icon="external-link-alt" class="me-2" />
              {{ invoice?.payment_link }}
            </a>
          </div>
  
        </div>
  
        <!-- ── Doc Footer ── -->
        <div class="invoice-doc-footer">
          <font-awesome-icon icon="check-circle" class="me-2" />
          Thank you for your business · {{ company?.name }}
        </div>
  
      </template>
    </div>
  </template>
  
  <script>
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import host from '../../../../main';
  
  export default {
    props: {
      invoice_items: Array,
      invoice_array: Array,
      invoice:       [Array, Object],
      loading:       Boolean,
    },
    mixins: [reusableMixin],
  
    data() {
      return {
        toast:   useToast(),
        store:   useStore(),
        company: null,
      };
    },
  
    computed: {
      host()        { return host.hostName; },
      loggedInUser(){ return this.store.state.getLoggedInUser; },
      grandTotal()  { return this.invoice?.total_amount; },
      subTotal()    { return this.invoice?.sub_total_amount; },
      discountTotal(){ return this.invoice?.discount_amount ?? 0; },
      taxTotal()    { return this.invoice?.tax_amount ?? 0; },
    },
  
    created() { this.getCompany(); },
  
    methods: {
      getStatus(status) {
        return ['Pending','Partial Payment','Completed','Overdue','Abandoned'][status] ?? '—';
      },
      statusClass(status) {
        return ['status--pending','status--partial','status--completed','status--overdue','status--abandoned'][status] ?? '';
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Document ── */
  .invoice-doc { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; font-size:13.5px; color:#1a2b45; }
  
  /* ── Loading ── */
  .invoice-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; color:#7a90b0; }
  .mt-3 { margin-top:12px; }
  
  /* ── Header ── */
  .invoice-header { display:flex; justify-content:space-between; align-items:flex-start; padding:32px 32px 24px; background:linear-gradient(135deg, #051B35 0%, #004AAD 100%); }
  .invoice-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:rgba(255,255,255,.6); margin:0 0 4px; }
  .invoice-number { font-size:28px; font-weight:900; color:#fff; margin:0 0 10px; }
  .invoice-status-chip { display:inline-block; border-radius:6px; padding:4px 12px; font-size:12px; font-weight:700; border:1px solid transparent; }
  .invoice-header-right { text-align:right; }
  .invoice-logo  { max-width:90px; max-height:60px; object-fit:contain; border-radius:8px; background:#fff; padding:4px; }
  .company-name  { font-size:13px; font-weight:700; color:rgba(255,255,255,.85); margin:8px 0 0; }
  
  /* Status colours (inverted for dark bg) */
  .status--pending   { background:rgba(224,140,0,.2);  color:#ffd27f; border-color:rgba(224,140,0,.4); }
  .status--partial   { background:rgba(99,172,255,.2); color:#99ccff; border-color:rgba(99,172,255,.4); }
  .status--completed { background:rgba(22,163,74,.2);  color:#6ee7a2; border-color:rgba(22,163,74,.4); }
  .status--overdue   { background:rgba(224,53,53,.2);  color:#fca5a5; border-color:rgba(224,53,53,.4); }
  .status--abandoned { background:rgba(255,255,255,.1); color:rgba(255,255,255,.6); border-color:rgba(255,255,255,.2); }
  
  /* ── Parties ── */
  .invoice-parties { display:grid; grid-template-columns:repeat(3,1fr); gap:0; border-bottom:1px solid #e4eaf2; }
  .party-card      { padding:24px 28px; border-right:1px solid #e4eaf2; }
  .party-card:last-child { border-right:none; }
  .party-label     { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:#7a90b0; margin:0 0 8px; }
  .party-name      { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 4px; text-transform:uppercase; }
  .party-detail    { font-size:13px; color:#4a5e78; margin:0 0 2px; }
  
  /* ── Meta rows ── */
  .meta-row  { display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; }
  .meta-key  { font-size:12.5px; color:#7a90b0; }
  .meta-val  { font-size:12.5px; font-weight:600; color:#1a2b45; }
  
  /* ── Line Items ── */
  .invoice-section { padding:0; }
  .inv-table       { width:100%; border-collapse:collapse; }
  .inv-table thead tr { background:#f8fafc; }
  .inv-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; border-top:1px solid #e4eaf2; white-space:nowrap; }
  .inv-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .inv-table tbody tr:last-child { border-bottom:none; }
  .inv-table tbody tr:hover { background:#fafbfc; }
  .inv-table tbody td { padding:12px 16px; font-size:13.5px; vertical-align:middle; }
  .item-desc   { font-weight:500; }
  .text-muted-cell { color:#4a5e78; }
  .tc  { text-align:center; }
  .tr  { text-align:right; }
  .fw-bold { font-weight:700; }
  .na-badge { font-size:11px; color:#aab8cc; background:#f4f7fb; border-radius:4px; padding:1px 6px; }
  
  /* ── Totals ── */
  .invoice-totals-wrap { display:flex; justify-content:flex-end; padding:20px 32px; border-top:1px solid #e4eaf2; background:#fafbfc; }
  .invoice-totals      { width:280px; display:flex; flex-direction:column; gap:8px; }
  .totals-row          { display:flex; justify-content:space-between; align-items:center; font-size:13.5px; }
  .totals-label        { color:#4a5e78; }
  .totals-value        { font-weight:600; color:#1a2b45; }
  .deduction-value     { color:#e03535; }
  .earning-value       { color:#16a34a; }
  .totals-divider      { height:1px; background:#e4eaf2; margin:4px 0; }
  .totals-row--grand   { }
  .totals-label-grand  { font-size:15px; font-weight:700; color:#1a2b45; }
  .totals-value-grand  { font-size:20px; font-weight:900; color:#004AAD; }
  
  /* ── Footer Sections ── */
  .invoice-footer-sections { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px,1fr)); gap:0; border-top:2px solid #e4eaf2; }
  .footer-section      { padding:24px 28px; border-right:1px solid #e4eaf2; }
  .footer-section:last-child { border-right:none; }
  .footer-section-title { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 14px; display:flex; align-items:center; }
  .footer-row          { display:flex; justify-content:space-between; align-items:center; font-size:13px; margin-bottom:8px; color:#4a5e78; }
  .footer-row--outstanding { background:rgba(224,53,53,.04); border-radius:8px; padding:6px 8px; margin-left:-8px; margin-right:-8px; }
  .me-2 { margin-right:7px; }
  
  .payment-link { display:inline-flex; align-items:center; font-size:12.5px; color:#004AAD; text-decoration:none; word-break:break-all; }
  .payment-link:hover { text-decoration:underline; }
  
  /* ── Doc Footer ── */
  .invoice-doc-footer { text-align:center; padding:16px 24px; background:#f8fafc; border-top:1px solid #e4eaf2; font-size:12.5px; color:#7a90b0; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .invoice-parties       { grid-template-columns:1fr; }
    .party-card            { border-right:none; border-bottom:1px solid #e4eaf2; }
    .party-card:last-child { border-bottom:none; }
    .invoice-totals        { width:100%; }
    .invoice-totals-wrap   { padding:16px 20px; }
    .invoice-footer-sections { grid-template-columns:1fr; }
    .footer-section        { border-right:none; border-bottom:1px solid #e4eaf2; }
    .footer-section:last-child { border-bottom:none; }
    .invoice-header        { flex-direction:column; gap:16px; padding:24px 20px 20px; }
    .invoice-header-right  { text-align:left; }
  }
  
  @media print {
    .invoice-doc { border:none; border-radius:0; box-shadow:none; }
  }
  </style>